/**
 * User details page with actions and edit form
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, ArrowLeft, Loader2, Save, User } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { UserActionsMenu } from "@/components/UserActionsMenu";
import { UserStatusBadge } from "@/components/UserStatusBadge";
import {
  useActivateUser,
  useDeleteUser,
  useDisableUser,
  useEnableUser,
  useSuspendUser,
  useUnlockUser,
  useUnsuspendUser,
  useUpdateUser,
  useUser,
} from "@/hooks/useUsers";

const updateUserSchema = z.object({
  fullName: z.string().optional(),
});

type UpdateUserFormData = z.infer<typeof updateUserSchema>;

export function UserDetails() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const { data: user, isLoading, error } = useUser(userId);
  const updateUser = useUpdateUser();
  const activateUser = useActivateUser();
  const suspendUser = useSuspendUser();
  const unsuspendUser = useUnsuspendUser();
  const disableUser = useDisableUser();
  const enableUser = useEnableUser();
  const unlockUser = useUnlockUser();
  const deleteUser = useDeleteUser();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<UpdateUserFormData>({
    resolver: zodResolver(updateUserSchema),
  });

  useEffect(() => {
    if (user) {
      reset({
        fullName: user.fullName || "",
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: UpdateUserFormData) => {
    if (!userId) {
      return;
    }
    await updateUser.mutateAsync({
      userId,
      input: { fullName: data.fullName || undefined },
    });
  };

  const handleDelete = async () => {
    if (!user || !userId) {
      return;
    }
    if (window.confirm(`Are you sure you want to delete user ${user.email}?`)) {
      await deleteUser.mutateAsync(userId);
      navigate("/users");
    }
  };

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/30">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <p className="ml-3 text-sm text-red-700 dark:text-red-300">
            {error instanceof Error ? error.message : "Failed to load user"}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/30">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-yellow-400" />
          <p className="ml-3 text-sm text-yellow-700 dark:text-yellow-300">User not found</p>
        </div>
      </div>
    );
  }

  const isActionPending =
    activateUser.isPending ||
    suspendUser.isPending ||
    unsuspendUser.isPending ||
    disableUser.isPending ||
    enableUser.isPending ||
    unlockUser.isPending ||
    deleteUser.isPending;

  return (
    <div>
      {/* Back link */}
      <Link
        to="/users"
        className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back to Users
      </Link>

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
            <User className="h-6 w-6 text-gray-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{user.email}</h1>
            <div className="mt-1 flex items-center space-x-2">
              <UserStatusBadge status={user.status} />
              {user.statusReason && (
                <span className="text-sm text-gray-500 dark:text-gray-400">({user.statusReason})</span>
              )}
            </div>
          </div>
        </div>

        <UserActionsMenu
          status={user.status}
          onActivate={() => activateUser.mutate(user.userId)}
          onSuspend={() => suspendUser.mutate({ userId: user.userId })}
          onUnsuspend={() => unsuspendUser.mutate(user.userId)}
          onDisable={() => disableUser.mutate({ userId: user.userId })}
          onEnable={() => enableUser.mutate(user.userId)}
          onUnlock={() => unlockUser.mutate(user.userId)}
          onDelete={handleDelete}
          isLoading={isActionPending}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* User Info */}
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">User Information</h2>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">User ID</dt>
              <dd className="mt-1 font-mono text-sm text-gray-900 dark:text-gray-100">{user.userId}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{user.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email Verified</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{user.emailVerified ? "Yes" : "No"}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {new Date(user.createdAt).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Updated</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {new Date(user.updatedAt).toLocaleString()}
              </dd>
            </div>
            {user.lastLoginAt && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Login</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {new Date(user.lastLoginAt).toLocaleString()}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Edit Form */}
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Edit User</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-4">
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                {...register("fullName")}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              {errors.fullName && <p className="mt-1 text-sm text-red-600">{errors.fullName.message}</p>}
            </div>

            {updateUser.error && (
              <div className="mb-4 rounded-md bg-red-50 p-3 dark:bg-red-900/30">
                <p className="text-sm text-red-700 dark:text-red-300">
                  {updateUser.error instanceof Error ? updateUser.error.message : "Update failed"}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={!isDirty || updateUser.isPending}
              className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {updateUser.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save Changes
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
